import React, { useCallback, useEffect, useRef, useState } from 'react'
import { post, del } from 'helpers/api_helper'
import { newPlayerOperationId } from './PlayerAccounts/playerInvitationState'

export function useEditorPhoto({ current, folder }) {
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [removed, setRemoved] = useState(false)
  const [error, setError] = useState('')
  const [uploading, setUploading] = useState(false)
  const uploaded = useRef(null), inFlight = useRef(null), alive = useRef(true)
  const owned = useRef(new Map()), retained = useRef(new Set())
  const cleanup = useCallback(async () => {
    for (const [filename, cleanupToken] of owned.current) {
      if (retained.current.has(filename)) continue
      if (!cleanupToken) continue
      try { await del('/assets/' + folder + '/' + filename, undefined, { headers: { 'X-Upload-Cleanup-Token': cleanupToken } }); owned.current.delete(filename) }
      catch { if (alive.current) setError('The unused upload could not be removed. Your current photo is unchanged.') }
    }
  }, [folder])
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false; cleanup() }
  }, [cleanup])
  useEffect(() => {
    if (!file) { setPreview(null); return undefined }
    const url = URL.createObjectURL(file); setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [file])
  const select = next => {
    setError('')
    if (!next) return
    if (!['image/png', 'image/jpeg'].includes(next.type) || next.size > 10 * 1024 * 1024) {
      setError('Choose a PNG or JPEG image of up to 10 MB.'); return
    }
    cleanup(); uploaded.current = null; setFile(next); setRemoved(false)
  }
  const upload = async () => {
    if (removed) return null
    if (!file) return current || null
    if (uploaded.current) return uploaded.current
    if (inFlight.current) return inFlight.current
    setUploading(true); setError('')
    inFlight.current = (async () => {
      try {
        const body = new FormData()
        body.append('file', file, 'profile-' + newPlayerOperationId() + (file.type === 'image/png' ? '.png' : '.jpg'))
        const response = await post('/assets/' + folder, body, { headers: { 'X-Profile-Upload': 'true' } })
        const filename = response?.data?.filename
        if (!/^[a-zA-Z0-9_-]+\.(?:png|jpe?g|webp)$/i.test(filename || '')) throw new Error('Upload unavailable')
        if (filename !== current) owned.current.set(filename, response.data.cleanupToken)
        if (!alive.current) { await cleanup(); throw new Error('PHOTO_UPLOAD_CANCELLED') }
        uploaded.current = filename
        return filename
      } catch (failure) {
        if (alive.current) setError('The photo could not be uploaded. Your current photo is unchanged. Try saving again.')
        throw failure
      } finally { inFlight.current = null; if (alive.current) setUploading(false) }
    })()
    return inFlight.current
  }
  const retain = useCallback(() => { for (const filename of owned.current.keys()) retained.current.add(filename) }, [])
  const release = useCallback(() => { retained.current.clear() }, [])
  return { select, upload, retain, release, cleanup, invalidateUpload: () => { uploaded.current = null }, receipt: filename => owned.current.get(filename), uploading, error, dirty: Boolean(file) || removed,
    preview: removed ? null : preview || (current ? process.env.REACT_APP_API_URL + '/assets/' + folder + '/' + current : null),
    remove: () => { cleanup(); setRemoved(true); setFile(null); uploaded.current = null; setError('') },
    reset: () => { cleanup(); setRemoved(false); setFile(null); uploaded.current = null; setError('') },
  }
}
export default function EditorPhoto({ photo, locked, label = 'Profile photo', id = 'editor-photo' }) {
  return <section className="card"><div className="card-body"><h5>{label}</h5>
    {photo.preview && <img src={photo.preview} alt={label + ' preview'} style={{ width: 120, height: 120, objectFit: 'cover' }} className="rounded mb-3" />}
    <label htmlFor={id}>{label}</label><input id={id} type="file" className="form-control-file" accept="image/png,image/jpeg" disabled={locked || photo.uploading}
      onChange={event => { photo.select(event.target.files?.[0]); event.target.value = '' }} />
    <small className="text-muted">PNG or JPEG, up to 10 MB. The image is uploaded when you save.</small>
    {photo.error && <p role="alert" className="text-danger">{photo.error}</p>}
    <div className="d-flex flex-wrap mt-2" style={{ gap: 10 }}>
      {photo.preview && <button type="button" className="btn btn-light" disabled={locked || photo.uploading} onClick={photo.remove}>Remove photo</button>}
      {photo.dirty && <button type="button" className="btn btn-light" disabled={locked || photo.uploading} onClick={photo.reset}>Keep current photo</button>}
    </div>
  </div></section>
}
